package com.essntl.features.organization.ui

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.PaginationState
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getPaginationData
import com.essntl.features.file.domain.repository.FileRepository
import com.essntl.features.file.ui.FileViewModel
import com.essntl.features.organization.domain.model.OrganizationModel
import com.essntl.features.organization.domain.repository.OrganizationRepository
import com.mohamedrejeb.calf.core.PlatformContext
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class OrganizationViewModel(
    private val organizationRepository: OrganizationRepository,
    private val fileRepository: FileRepository,
    private val state: MutableStateFlow<OrganizationState>,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {

    private val fileViewModel = FileViewModel(
        fileRepository = fileRepository,
        scope = scope,
        loadingManager = loadingManager,
    )

    init {
        collectFileState()
    }

    private var getLogoJob: Job? = null
    private fun collectFileState() {
        // collecting logos
        getLogoJob?.cancel()
        getLogoJob = scope.launch {
            fileViewModel.state.collect { data ->
                state.update {
                    it.copy(
                        fileState = data,
                    )
                }
            }
        }
    }

    fun onEvent(event: OrganizationEvent) {
        when (event) {
            is OrganizationEvent.LoadOrganizations -> getNextOrganizationPage(organization = event.organization)
            is OrganizationEvent.SearchOrganization -> {
                state.value = state.value.copy(getAllOrganizations = PaginationState(query = event.query))
                getNextOrganizationPage()
            }

            is OrganizationEvent.UpdateOrganizationFields -> updateOrganizationFields(event)
            is OrganizationEvent.SetParentOrganization -> setParentOrganization(event)
            is OrganizationEvent.AddLogo -> addLogo(event)
            is OrganizationEvent.DeleteLogo -> deleteLogo()
            is OrganizationEvent.InsertLogo -> insertFiles(event.context)
            is OrganizationEvent.ClearLogo -> clearLogo()

            is OrganizationEvent.ClearOrganizationState -> clearOrganizationState()
            is OrganizationEvent.AddRemoveAddress -> addRemoveAddress(event)
        }
    }

    private fun addLogo(event: OrganizationEvent.AddLogo) {
        if (event.file.id.isNotEmpty()) state.value.organization.value = state.value.organization.value.copy(
            logoFileId = event.file.id,
        )

        fileViewModel.addFiles(listOf(event.file))
    }

    private fun deleteLogo() {
        state.value.organization.value = state.value.organization.value.copy(
            logoFileId = null,
        )

        fileViewModel.deleteFile(0)
    }

    private fun updateOrganizationFields(event: OrganizationEvent.UpdateOrganizationFields) {
        state.value.organization.value = event.organization
    }

    private fun setParentOrganization(event: OrganizationEvent.SetParentOrganization) {
        state.value.selectedParentOrganization.value = event.organization
        state.value.organization.value = state.value.organization.value.copy(
            parentId = event.organization.id
        )
    }

    private fun getNextOrganizationPage(
        organization: OrganizationModel? = null,
        query: String = state.value.getAllOrganizations.query,
    ) {
        if (organization != null) {

            val newTravelAgent = state.value.getAllOrganizations.data.find {
                it.id == organization.id
            }

            if (newTravelAgent != null) {
                // Edit travel agent
                state.update {
                    it.copy(
                        getAllOrganizations = it.getAllOrganizations.copy(
                            data = it.getAllOrganizations.data.map { ta ->
                                if (ta.id == organization.id) {
                                    organization
                                } else {
                                    ta
                                }
                            },
                        ),
                    )
                }
            } else {
                // Add new travel agent
                onEvent(OrganizationEvent.SearchOrganization(""))
            }
        } else {
            getPaginationData(
                getData = {
                    val nextPage = state.value.getAllOrganizations.loadedPages + 1L

                    organizationRepository.getAll(
                        page = nextPage,
                        query = query,
                        type = state.value.organization.value.type,
                    )
                },
                getState = { state.value.getAllOrganizations },
                setState = { state.value = state.value.copy(getAllOrganizations = it) },
            )
        }
    }

    private fun addRemoveAddress(event: OrganizationEvent.AddRemoveAddress) =
        state.update {
            it.copy(
                addAddress = event.addAddress ?: !it.addAddress,
            )
        }

    private fun clearOrganizationState() {
        state.update {
            it.copy(
                addAddress = false,
            )
        }.also {
            state.value.organization.value = OrganizationModel.empty
        }

        fileViewModel.setState(State())
    }

    fun insertFiles(context: PlatformContext) =
        fileViewModel.insertOrganizationLogos(
            setState = { newState ->
                state.update {
                    it.copy(
                        uploadLogo = newState,
                    )
                }
            },
            onSuccess = { files ->
                state.value.organization.value = state.value.organization.value.copy(
                    logoFileId = files.first().id,
                )
            },
            folderName =
                if (state.value.organization.value.type == OrganizationModel.Type.TravelAgency)
                    "ta_logos" else
                    "supplier_logos",
            entityType = if (state.value.organization.value.type == OrganizationModel.Type.TravelAgency)
                "travel_agent" else
                "supplier",
            organizationName = state.value.organization.value.name,
            context = context,
        )

    fun clearLogo() =
        state.update {
            it.copy(
                uploadLogo = State(),
            )
        }
}